import React, { useEffect, useRef, useState } from 'react';
import { safeHref } from '../../utils/common/safe-url';
import toast from 'react-hot-toast';
import { MoreHorizontal, Flag, EyeOff, X } from 'lucide-react';
import {
  AdCreative,
  serveAds,
  recordAdEvent,
  detectDevice,
  reportAd,
  AD_REPORT_REASONS,
} from '../../api/ads/ads.api';

// deviceId ổn định (chống gian lận click phía server).
const getDeviceId = () => {
  try {
    let id = localStorage.getItem('cab-device-id');
    if (!id) {
      id = `d_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem('cab-device-id', id);
    }
    return id;
  } catch {
    return undefined;
  }
};

// Danh sách quảng cáo user đã ẩn (giữ giữa các phiên).
const HIDDEN_KEY = 'cab-hidden-ads';
export const getHiddenIds = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(HIDDEN_KEY) || '[]');
  } catch {
    return [];
  }
};
const addHiddenId = (id: string) => {
  try {
    const s = new Set(getHiddenIds());
    s.add(id);
    localStorage.setItem(HIDDEN_KEY, JSON.stringify([...s]));
  } catch {
    /* ignore */
  }
};

export const IMPRESSION_MIN_RATIO = 0.5;
export const IMPRESSION_MIN_MS = 1000;

interface NativeAdCardProps {
  categoryId?: string;
  placement?: 'Feed' | 'Reels' | 'Video' | 'Profile';
  creatorId?: string;
  postId?: string;
  variant?: 'card' | 'reel';
  /** Quảng cáo đã lấy sẵn (Reels): không serve lại. */
  initialAd?: AdCreative;
}

/**
 * Thẻ quảng cáo Native chèn vào feed — tự lấy 1 quảng cáo hợp lệ (serve), render như 1 bài "Được tài trợ",
 * ghi HIỂN THỊ khi vào khung nhìn, ghi CLICK khi bấm. Người xem có thể BÁO CÁO hoặc ẨN quảng cáo.
 */
const NativeAdCard: React.FC<NativeAdCardProps> = ({
  categoryId,
  placement = 'Feed',
  creatorId,
  postId,
  variant = 'card',
  initialAd,
}) => {
  const [ad, setAd] = useState<AdCreative | null>(initialAd ?? null);
  const [hidden, setHidden] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reason, setReason] = useState(AD_REPORT_REASONS[0].value);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const impressedRef = useRef(false);

  useEffect(() => {
    if (initialAd) return;
    let alive = true;
    serveAds({
      categoryId,
      device: detectDevice(),
      placement,
      count: 1,
      ...(creatorId ? { creatorId } : {}),
      ...(postId ? { postId } : {}),
    })
      .then((res) => {
        if (!alive || !res?.length) return;
        if (getHiddenIds().includes(res[0].campaignId)) return; // đã ẩn thì bỏ qua
        setAd(res[0]);
      })
      .catch(() => {
        /* im lặng: không có ads thì thôi */
      });
    return () => {
      alive = false;
    };
  }, [categoryId, placement, creatorId, postId, initialAd]);

  // Ghi HIỂN THỊ 1 lần: thẻ hiện ≥ 50% LIÊN TỤC ≥ 1 giây và tab đang hiển thị.
  useEffect(() => {
    if (!ad || !ref.current) return;
    const el = ref.current;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inView = false;
    const clear = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };
    const fire = () => {
      timer = null;
      if (impressedRef.current) return;
      impressedRef.current = true;
      recordAdEvent({
        campaignId: ad.campaignId,
        creativeId: ad.creativeId,
        type: 'Impression',
        deviceId: getDeviceId(),
        serveToken: ad.serveToken,
      }).catch(() => undefined);
      cleanup();
    };
    const evaluate = () => {
      if (impressedRef.current) return;
      if (inView && document.visibilityState === 'visible') {
        if (!timer) timer = setTimeout(fire, IMPRESSION_MIN_MS);
      } else {
        clear();
      }
    };
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          inView = e.isIntersecting && e.intersectionRatio >= IMPRESSION_MIN_RATIO;
        });
        evaluate();
      },
      { threshold: [0, IMPRESSION_MIN_RATIO, 1] }
    );
    document.addEventListener('visibilitychange', evaluate);
    function cleanup() {
      clear();
      obs.disconnect();
      document.removeEventListener('visibilitychange', evaluate);
    }
    obs.observe(el);
    return cleanup;
  }, [ad]);

  const onClick = () => {
    if (!ad || menuOpen || reportOpen) return;
    recordAdEvent({
      campaignId: ad.campaignId,
      creativeId: ad.creativeId,
      type: 'Click',
      deviceId: getDeviceId(),
      serveToken: ad.serveToken,
    }).catch(() => undefined);
    if (ad.landingUrl) window.open(safeHref(ad.landingUrl), '_blank', 'noopener,noreferrer');
  };

  const hideAd = () => {
    if (ad) addHiddenId(ad.campaignId);
    setMenuOpen(false);
    setHidden(true);
  };

  const submitReport = async () => {
    if (!ad) return;
    try {
      setSending(true);
      const res = await reportAd({
        campaignId: ad.campaignId,
        creativeId: ad.creativeId,
        reason,
        note: note.trim() || undefined,
      });
      toast.success(res.message || 'Đã gửi báo cáo. Cảm ơn bạn!');
      if (ad) addHiddenId(ad.campaignId);
      setReportOpen(false);
      setHidden(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Gửi báo cáo thất bại.');
    } finally {
      setSending(false);
    }
  };

  if (!ad || hidden) return null;

  return (
    <div
      ref={ref}
      data-testid="creator-ad"
      className={
        variant === 'reel'
          ? 'relative h-full w-full flex flex-col justify-center overflow-y-auto bg-white dark:bg-dark2 p-6 cursor-pointer'
          : 'box relative border border-[#DEE0E6] dark:border-slate-700 bg-white dark:bg-dark2 rounded-xl p-4 cursor-pointer'
      }
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-wide text-[#657384] dark:text-slate-400">
          {ad.sponsored || 'Được tài trợ'}
        </span>
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-[#657384] dark:text-slate-300">
            Quảng cáo
          </span>
          <button
            type="button"
            aria-label="Tuỳ chọn quảng cáo"
            onClick={() => setMenuOpen((v) => !v)}
            className="p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/10 text-[#657384] dark:text-slate-300"
          >
            <MoreHorizontal size={16} />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-8 z-20 w-52 rounded-lg border border-[#DEE0E6] dark:border-slate-700 bg-white dark:bg-dark2 shadow-lg py-1 text-sm">
                <button
                  className="w-full text-left px-3 py-2 hover:bg-black/5 dark:hover:bg-white/10 flex items-center gap-2 ui-text"
                  onClick={() => {
                    setMenuOpen(false);
                    setReportOpen(true);
                  }}
                >
                  <Flag size={14} /> Báo cáo quảng cáo
                </button>
                <button
                  className="w-full text-left px-3 py-2 hover:bg-black/5 dark:hover:bg-white/10 flex items-center gap-2 ui-text"
                  onClick={hideAd}
                >
                  <EyeOff size={14} /> Ẩn quảng cáo này
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {ad.title ? (
        <h3 className="font-semibold text-[15px] ui-text mb-1">{ad.title}</h3>
      ) : null}
      {ad.body ? <p className="text-sm ui-muted mb-2">{ad.body}</p> : null}

      {ad.format === 'Video' && ad.videoUrl ? (
        <video
          src={ad.videoUrl}
          className="w-full rounded-lg max-h-[70vh] object-cover"
          muted
          autoPlay
          loop
          playsInline
        />
      ) : ad.imageUrl ? (
        <img src={ad.imageUrl} alt={ad.title || 'ad'} className="w-full rounded-lg object-cover" />
      ) : null}

      <div className="mt-3">
        <span className="inline-flex items-center rounded-lg bg-[#0071A6] text-white text-sm font-medium px-4 py-2">
          {ad.ctaText || 'Tìm hiểu thêm'}
        </span>
      </div>

      {/* Dialog báo cáo */}
      {reportOpen && (
        <div
          className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4"
          onClick={(e) => {
            e.stopPropagation();
            setReportOpen(false);
          }}
        >
          <div
            className="w-full max-w-sm rounded-xl bg-white dark:bg-dark2 p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold ui-text">Báo cáo quảng cáo</h3>
              <button onClick={() => setReportOpen(false)} className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/10">
                <X size={18} className="ui-text" />
              </button>
            </div>
            <p className="text-xs ui-muted mb-3">Vì sao bạn báo cáo quảng cáo này?</p>
            <div className="space-y-1.5 mb-3">
              {AD_REPORT_REASONS.map((r) => (
                <label key={r.value} className="flex items-center gap-2 text-sm ui-text cursor-pointer">
                  <input
                    type="radio"
                    name="ad-report-reason"
                    checked={reason === r.value}
                    onChange={() => setReason(r.value)}
                  />
                  {r.label}
                </label>
              ))}
            </div>
            <textarea
              className="w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none resize-none ui-text"
              rows={2}
              placeholder="Mô tả thêm (tuỳ chọn)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <button
              onClick={submitReport}
              disabled={sending}
              className="mt-3 w-full rounded-lg bg-[#DC2626] text-white text-sm font-medium py-2 disabled:opacity-50"
            >
              {sending ? 'Đang gửi…' : 'Gửi báo cáo'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NativeAdCard;
